// Tiny localStorage-backed wishlist + recently-viewed store. Both work
// per-browser, no auth required, so anonymous shoppers can save without
// signing in. There are no shopper accounts on the PlazaStack storefront.
//
// Events:
//   'np:wishlist-changed' — fires after add/remove so any subscribed
//     component (e.g. count badge in header) can re-read.

export const WISHLIST_KEY      = 'np:wishlist';
export const RECENT_KEY        = 'np:recently-viewed';
export const WISHLIST_CHANGED  = 'np:wishlist-changed';

export type StoredProduct = {
  /** Product slug (the /shop/<id> URL segment). Older entries may hold a Wix UUID; those 301 to the slug. */
  id:    string;
  name:  string;
  price: string;
  image?: string;
  /** Set for single-variant, buyable products so "Move to bag" can add without a size pick. */
  variantId?: string;
};

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try { return JSON.parse(raw) as T; } catch { return fallback; }
}

function dispatchChange() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(WISHLIST_CHANGED));
}

export function getWishlist(): StoredProduct[] {
  if (typeof window === 'undefined') return [];
  return safeParse<StoredProduct[]>(window.localStorage.getItem(WISHLIST_KEY), []);
}

export function isInWishlist(id: string): boolean {
  return getWishlist().some((p) => p.id === id);
}

export function toggleWishlist(item: StoredProduct): boolean {
  if (typeof window === 'undefined') return false;
  const list = getWishlist();
  const idx  = list.findIndex((p) => p.id === item.id);
  let nowIn  = false;
  if (idx >= 0) {
    list.splice(idx, 1);
  } else {
    list.unshift(item);
    nowIn = true;
  }
  window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(list.slice(0, 100)));
  dispatchChange();
  return nowIn;
}

// Recently viewed — capped at 12, dedup by id, newest first.
export function pushRecentlyViewed(item: StoredProduct): void {
  if (typeof window === 'undefined') return;
  const cur = safeParse<StoredProduct[]>(window.localStorage.getItem(RECENT_KEY), []);
  const without = cur.filter((p) => p.id !== item.id);
  const next    = [item, ...without].slice(0, 12);
  window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
}

export function getRecentlyViewed(): StoredProduct[] {
  if (typeof window === 'undefined') return [];
  return safeParse<StoredProduct[]>(window.localStorage.getItem(RECENT_KEY), []);
}
